/**
 * AgentMarketplacePageV2 — proper v2 redesign of Agent Marketplace (G113).
 *
 * Canonical inventory pattern.
 * Reuses every backend code path from v1 AgentMarketplacePage:
 *   - MARKETPLACE_AGENTS / EXPERTISE_LEVELS / AGENT_ROLE_CATEGORIES (data)
 *   - useMarketplaceCatalogue() registry hook
 *   - usePlatform() createAgent / addSkill / etc.
 *
 * v1 stays as fallback via /agent-marketplace?legacy=1.
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatform } from '../contexts/PlatformContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { isRebirthBuildSectionEnabled } from '../utils/featureFlags';
import {
  AGENT_ROLE_CATEGORIES,
  EXPERTISE_LEVELS,
  MARKETPLACE_AGENTS,
  getAgentStats,
  // RC-61 — Connector swap UX. Lets the operator pick an alternate
  // connector in the same category before install (e.g. Splunk →
  // Elastic, Slack → Teams). Same map V1 used; lives in the same
  // module so V2 mirrors V1 behaviour without a separate fixture.
  CONNECTOR_ALTERNATIVES,
} from '../data/agentMarketplace';
import { useMarketplaceCatalogue } from '../data/marketplaceRegistry';
import {
  RefreshCw, Plus, Store, Bot, Layers, Award, CheckCircle, ExternalLink,
  Search, X,
} from 'lucide-react';
import {
  Button, Pill, Card, Modal, Slideout, Table, FacetRail, EmptyState,
  MetricCard,
} from '../components/v2';
import { Pagination, usePaginationState } from '../components/common/Pagination';

import AgentMarketplacePage from './AgentMarketplacePage';

/* RC-71 — Tone maps and badge helpers.
 *
 * V1 (AgentMarketplacePage.jsx) shipped bespoke styled <span> badges
 * for tier / expertise / severity / eval-type / skill-type. V2's Pill
 * primitive already encodes the design-token mapping, so we collapse
 * V1's hand-rolled palettes into Pill tone lookups. Lookup tables are
 * tolerant — unknown keys fall back to `muted` so the badge always
 * renders something rather than swallowing the row. `foundational` is
 * added to EXPERTISE_TONE alongside the legacy `beginner` so the
 * fixture's actual values (foundational/intermediate/advanced/expert)
 * map correctly and the slideout header doesn't degrade to muted. */
const EXPERTISE_TONE = {
  foundational: 'cyan',
  beginner: 'cyan',
  intermediate: 'action',
  advanced: 'trust',
  expert: 'warning',
};

const TIER_TONE = {
  starter: 'success',
  premium: 'trust',
  enterprise: 'warning',
};

const SEVERITY_TONE = {
  critical: 'danger',
  high:     'warning',
  medium:   'brand',
  low:      'cyan',
};

const EVAL_TYPE_TONE = {
  functional: 'action',
  negative:   'warning',
  guardrail:  'rose',
  security:   'danger',
  cost:       'success',
};

/* Maps the raw `type` enum on a linkedSkills entry to a (tone,label)
 * pair. V1's SkillTypeBadge surfaces "Connector" / "Workflow" / "Policy"
 * / "Standard" — never the raw enum value. The pre-RC-71 V2 Skills tab
 * rendered "connector_skill" / "standard" literally inside a cyan pill,
 * which read as developer enum leaking into the operator surface. */
const SKILL_TYPE_META = {
  standard:        { tone: 'muted', label: 'Standard' },
  connector_skill: { tone: 'cyan',  label: 'Connector' },
  workflow_skill:  { tone: 'brand', label: 'Workflow' },
  policy_skill:    { tone: 'rose',  label: 'Policy' },
};

function TierBadge({ tier }) {
  if (!tier) return null;
  return <Pill tone={TIER_TONE[tier] || 'muted'} size="sm">{tier}</Pill>;
}

function SeverityBadge({ severity }) {
  if (!severity) return null;
  return <Pill tone={SEVERITY_TONE[severity] || 'muted'} size="sm">{severity}</Pill>;
}

function EvalTypeBadge({ type }) {
  if (!type) return null;
  return <Pill tone={EVAL_TYPE_TONE[type] || 'muted'} size="sm">{type}</Pill>;
}

function SkillTypeBadge({ type }) {
  const meta = SKILL_TYPE_META[type] || { tone: 'muted', label: type || 'skill' };
  return <Pill tone={meta.tone} size="sm">{meta.label}</Pill>;
}

/* ConnectorChip — visual identity for a single connector reference.
 * Resolves the id through useMarketplaceCatalogue's connectors list
 * (passed in as `connectorsById` so the chip itself stays presentational
 * and reusable). When the registry doesn't know the id (offline /
 * stale catalogue / future connector), the chip falls back to the raw
 * id string so the operator still sees *something*. Mirrors V1's
 * ConnectorChip (lines 163-178 of AgentMarketplacePage.jsx). */
function ConnectorChip({ connectorId, connectorsById }) {
  const conn = connectorsById?.get?.(connectorId);
  const icon = conn?.icon || '🔗';
  const name = conn?.name || connectorId;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-v2-muted text-v2-ink-secondary rounded-v2-pill px-2 py-0.5 border border-v2-border-subtle">
      <span aria-hidden>{icon}</span>{name}
    </span>
  );
}

function AgentMarketplacePageV2Inner() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const platform = usePlatform();
  const { session } = useAuth();
  const orgId = session?.orgId;
  const tenantId = session?.tenantId;
  const catalogue = useMarketplaceCatalogue?.() || { connectors: [] };

  const agents = useMemo(() => MARKETPLACE_AGENTS || [], []);

  /* RC-64 — Hydrate installed-agent state so the marketplace can show
   * which catalog entries the tenant has already installed and what
   * state those installs are in (build / decide / act, active / draft).
   *
   * Pre-RC-64 V2 only rendered the static MARKETPLACE_AGENTS catalogue,
   * so an operator browsing the marketplace had no signal whether they
   * had already installed a given agent or what its operational state
   * was — they had to bounce out to /agents to check. V1 surfaced a
   * binary "✓ INSTALLED" badge but no status detail; this restores
   * that visibility AND extends it with the phase/status pills the
   * V2 design system already exposes elsewhere.
   *
   * Matching strategy: case-insensitive NAME match against the
   * marketplace catalog. The install flow defaults installName to
   * a.name (and the operator usually accepts it), so most installs
   * match by name. Renamed installs are a soft miss — they render as
   * "not installed" in the marketplace until a future BE field
   * (sourceMarketplaceId or similar) lets us cross-reference by id.
   * Defensive: loadAgents failures (auth/permission/network) leave
   * the lookup empty so every row falls back to pre-RC-64 behaviour. */
  /* RC-91 → RC-93 — DEFENSIVE IDEMPOTENT FETCH for the installed-state
   * hydration.
   *
   * RC-91 was the first fix: removed `platform` from the dep array so
   * context-object identity churn wouldn't re-fire the effect. That
   * fixed the *known* loop trigger. RC-92a then memoized
   * PlatformContext.value to make the structural pattern safe for all
   * consumers.
   *
   * But staging log (07 Jun 2026, agentlets-staging-frontend) showed
   * the loop STILL hitting ~50–100 /api/v1/agents/sec from this V2
   * page after a clean rebuild — DevTools initiator stack pointed at
   * this file. That means SOMETHING upstream is still re-firing this
   * effect at render rate. Candidates we haven't proven yet:
   *   - AuthProvider.value is not memoized — every render produces a
   *     new context object, which is fine for our deps (orgId/tenantId
   *     are strings) but bad if any nested useState ever mutates them
   *     to a transient undefined/empty value.
   *   - StrictMode + hot-reload double-invocation pathology in dev.
   *   - A sibling component on the same route mass-re-rendering us.
   *
   * Rather than keep playing whack-a-mole at the dep-array level, we
   * make the fetch ITSELF idempotent per scope using a ref. The fetch
   * only fires when (orgId, tenantId) is a SCOPE WE HAVEN'T ALREADY
   * FETCHED, regardless of how often React calls the effect. This is
   * the same guard a queue-once cache library would impose; we inline
   * it because it's 3 lines and needs no library.
   *
   * Trade-off: scope-change refresh still works (different
   * scopeKey → new fetch). On the same scope a manual refresh button
   * can call platform.loadAgents directly and bypass this guard. Page
   * unmount/remount resets the ref so navigating away and back will
   * re-fetch — which is also the correct behaviour. */
  const lastFetchedScopeRef = useRef(null);
  useEffect(() => {
    if (!orgId || !tenantId) return;
    if (typeof platform?.loadAgents !== 'function') return;
    const scopeKey = `${orgId}::${tenantId}`;
    if (lastFetchedScopeRef.current === scopeKey) return;
    lastFetchedScopeRef.current = scopeKey;
    platform.loadAgents({ orgId, tenantId }).catch(() => {
      /* silent — marketplace is browsable without installed state.
       * On failure we reset the ref so the next render attempt can
       * try again (otherwise a transient 401/network blip would
       * leave us permanently un-hydrated until the user navigates). */
      lastFetchedScopeRef.current = null;
    });
    // NOTE: `platform` deliberately omitted from deps (RC-91 trap).
    // CRA's preset doesn't register react-hooks/exhaustive-deps so
    // we can't suppress with a targeted disable comment (RC-65 trap).
  }, [orgId, tenantId]);

  const installedByName = useMemo(() => {
    const map = new Map();
    const list = Array.isArray(platform?.agents) ? platform.agents : [];
    for (const a of list) {
      if (!a?.name) continue;
      // Last-write-wins on duplicates so a freshly-installed agent
      // replaces an older soft-deleted one of the same name.
      map.set(String(a.name).trim().toLowerCase(), a);
    }
    return map;
  }, [platform?.agents]);

  /* RC-64 — Helper that maps an installed agent's phase + status to a
   * Pill { tone, label } pair. Pure function; no defaults reach JSX.
   * Order of precedence: archived/draft (cool tones) → phase (warm).
   * Returns null when there's nothing meaningful to show. */
  const installedStatusPill = useCallback((inst) => {
    if (!inst) return null;
    const status = String(inst.status || '').toLowerCase();
    if (status === 'archived') return { tone: 'muted', label: 'Archived' };
    if (status === 'draft')    return { tone: 'muted', label: 'Draft' };
    const phase = String(inst.phase || '').toLowerCase();
    if (phase === 'act')    return { tone: 'success', label: 'Act' };
    if (phase === 'decide') return { tone: 'action',  label: 'Decide' };
    if (phase === 'build')  return { tone: 'warning', label: 'Build' };
    return { tone: 'cyan', label: 'Installed' };
  }, []);

  /* G206 — V2-native Install flow. Mirrors v1 handleInstall but lighter:
   * we don't ship a full wizard (connector-swap UX is parked); we just
   * create the agent + provision its skills, then route the operator to
   * the new agent detail page so they finish configuring connectors there. */
  const [installCandidate, setInstallCandidate] = useState(null);
  const [installName, setInstallName] = useState('');
  const [installing, setInstalling] = useState(false);
  const [installStep, setInstallStep] = useState('');
  const [installError, setInstallError] = useState(null);
  /* RC-61 — Connector swap state for the install modal.
   * Map of { originalConnectorId → selectedAlternativeId }. Empty
   * means "use defaults"; an entry means the operator picked an
   * alternate from the same category (e.g. {'splunk-v1': 'elastic-security-v1'}).
   * Reset on every openInstall so a previous candidate's selections
   * don't leak into the next install. */
  const [connectorSelections, setConnectorSelections] = useState({});
  /* RC-61 — Derived "swap categories" for the current install
   * candidate. One entry per unique category among the candidate's
   * linkedConnectors. Each entry: { category, defaultId, peers }.
   * Empty list when (a) no candidate is open, (b) candidate has no
   * connectors, or (c) none of its connectors appear in
   * CONNECTOR_ALTERNATIVES — in which case the picker UI hides itself
   * and the install button behaves exactly as it did pre-RC-61. */
  const connectorSwapCategories = useMemo(() => {
    if (!installCandidate) return [];
    const connectors = installCandidate.linkedConnectors
                    || installCandidate.requiredConnectors
                    || installCandidate.connectors
                    || [];
    if (!Array.isArray(connectors) || connectors.length === 0) return [];
    const seen = {};
    for (const cId of connectors) {
      // cId may be a string (fixture shape) or {id,name} (future BE shape).
      const key = typeof cId === 'string' ? cId : (cId && cId.id);
      if (!key) continue;
      const alt = CONNECTOR_ALTERNATIVES?.[key];
      if (!alt) continue;
      if (!seen[alt.category]) {
        seen[alt.category] = { category: alt.category, defaultId: key, peers: alt.peers || [key] };
      }
    }
    return Object.values(seen);
  }, [installCandidate]);
  /* RC-61 — Resolve a connector through the current swap selection.
   * Used inside runInstall when stamping s.connector on each skill
   * payload, so the BE receives the operator's chosen alternate
   * instead of the canonical connector id. Pure function of the
   * selections map; defaults safely when an id has no entry. */
  const resolveConnector = useCallback((originalId) => {
    if (!originalId) return originalId;
    return connectorSelections[originalId] || originalId;
  }, [connectorSelections]);
  const openInstall = (a) => {
    setInstallCandidate(a);
    setInstallName(a?.name || '');
    setInstallStep('');
    setInstallError(null);
    /* RC-71c — Removed the connectorSelections reset that used to live
     * here. The reset now lives in a single effect keyed on selected.id
     * so it fires exactly when the agent changes, not every time the
     * install tab opens. This lets the operator swap a connector on the
     * Connectors tab and click the header Install button without losing
     * the swap (RC-71 → install path) — which was the bug screenshot
     * shipped during the RC-71 verification. Row Install for a
     * *different* agent still resets correctly because setSelected(a)
     * below triggers the same effect with a new id. */
    /* RC-63 — Single-surface UX. The Install button on a row used to
     * open a separate Modal — that meant browse (Slideout) and install
     * (Modal) were two different surfaces, the connector picker only
     * appeared inside the Modal (so operators couldn't see it existed
     * from the browse view), and there were TWO Install entry points
     * with no clear primary. Per operator feedback, the install form
     * now lives inside the Slideout's Install tab. This handler opens
     * the slideout to that tab in one click so the row's green Install
     * button still works as the fast-path. */
    setSelected(a);
    setSlideoutTab('install');
  };
  const closeInstall = () => {
    if (installing) return;
    setInstallCandidate(null);
    setInstallError(null);
    setInstallStep('');
    setConnectorSelections({});
  };
  /* RC-66 — The RC-63 auto-sync + slideout-close useEffects were
   * originally inlined here, but they reference `selected` and
   * `slideoutTab` in their dependency arrays — those state hooks are
   * declared further down the component body. The deps array is
   * evaluated synchronously when React encounters the useEffect call,
   * so the in-TDZ references threw "Cannot access 've' before
   * initialization" at the first render. Moved both effects to
   * AFTER the [selected, slideoutTab] useState lines below; see the
   * comment block tagged "RC-66 — relocated effects" further down. */
  const runInstall = async () => {
    const a = installCandidate;
    if (!a) return;
    setInstalling(true);
    setInstallError(null);
    setInstallStep(`Creating agent "${installName}"…`);
    try {
      const agentPayload = {
        name: installName.trim() || a.name,
        type: 'cognitive',
        phase: 'build',
        status: 'active',
        orgId, tenantId,
        icon: a.icon || '🤖',
      };
      const created = await platform.createAgent?.(agentPayload);
      if (!created || created.ok === false) {
        setInstallError(created?.error || created?.data?.error || 'Failed to create agent.');
        return;
      }
      const newAgentId = created.data?.id;
      if (!newAgentId) {
        setInstallError('Agent created but no id returned.');
        return;
      }
      /* MARKETPLACE_AGENTS shape: skills can be array of { name, kind } or
       * strings. linkedSkills / linkedWorkflows are richer on v1; here we
       * just provision name+description placeholders so the operator can
       * fill the bodies in the agent detail page. */
      const skillEntries = Array.isArray(a.linkedSkills) && a.linkedSkills.length > 0
        ? a.linkedSkills
        : (a.skills || []);
      for (let i = 0; i < skillEntries.length; i++) {
        const s = skillEntries[i];
        const sname = s.name || (typeof s === 'string' ? s : `Skill ${i + 1}`);
        setInstallStep(`Provisioning skill ${i + 1} of ${skillEntries.length}: ${sname}…`);
        /* RC-71d — Do NOT include connectorId in the Skill payload.
         *
         * The BE Skill entity (model/Skill.java) has NO connectorId
         * column — connector binding lives in a separate link table,
         * reached via POST /api/v1/agents/{agentId}/skills/{skillId}/connectors.
         * Skill is annotated only with Lombok + @Entity, so it does
         * not carry @JsonIgnoreProperties(ignoreUnknown=true), and
         * Spring Boot 3.2's Jackson defaults FAIL_ON_UNKNOWN_PROPERTIES
         * to true (see comment block on AppUser.java). Sending the
         * field made every connector_skill row fail with HTTP 400
         * "Request body could not be parsed" — half the Phishing
         * Email Analyzer install (4 of 8 skills) silently dropped.
         *
         * The proper connector-binding endpoint is separate and has
         * extra prerequisites we cannot satisfy at install time:
         *   (a) it expects a real connector UUID, but the marketplace
         *       fixture uses string ids ("virustotal-v1", "misp-v1");
         *   (b) the connector must first be assigned to the agent via
         *       Team Management (connectorAssignmentService.assigned).
         * Both are post-install steps the operator drives from the
         * Agent Lifecycle page, which is exactly where G206 / the
         * original V2 design pointed them ("Configure connectors
         * next." in the success toast).
         *
         * We still HONOR the operator's swap choices made on the
         * Connectors tab — they're surfaced in the success toast so
         * the operator knows which connector to wire up post-install
         * (e.g. "Configure these connectors: MISP, Microsoft Defender
         * for Office 365, ServiceNow, Email (SMTP)"). The selection
         * persists in connectorSelections so a later install-wizard
         * iteration could replay it directly into the link endpoint.
         */
        const resolvedConnector = resolveConnector(s.connector);
        const skillPayload = {
          name: sname,
          description: s.description || `Pre-built skill from ${a.name} marketplace agent`,
          category: s.category || 'analysis',
          riskLevel: 'medium',
          autonomy: 'HUMAN_CONTROLLED',
          inferenceMode: 'PLATFORM_INFERENCE',
          guardrails: {},
          skillType: s.type || 'standard',
        };
        // resolvedConnector is intentionally unused in the payload — see
        // RC-71d comment above. Kept in scope so the swap choice still
        // contributes to the post-install toast guidance below.
        void resolvedConnector;
        const sr = await platform.addSkill?.(newAgentId, skillPayload);
        if (sr && sr.ok === false) {
          /* Continue on per-skill failure but surface a banner — the agent
           * still exists and is usable. */
          addToast({ tone: 'warning', title: `Skill "${sname}" failed`, description: sr?.error || 'Backend rejected payload' });
        }
      }
      setInstallStep('Finalizing…');
      /* RC-71d — Surface the connector picks (registry-resolved display
       * names, with swap overrides applied) in the success toast so the
       * operator knows exactly what to wire up on the Agent Lifecycle
       * page next. Pre-RC-71d the toast said only "Configure connectors
       * next." with no hint of which ones. */
      const connectorList = (a.linkedConnectors || a.requiredConnectors || a.connectors || [])
        .map(cId => {
          const key = typeof cId === 'string' ? cId : cId?.id;
          if (!key) return null;
          const resolved = connectorSelections[key] || key;
          const conn = connectorsById.get(resolved);
          const label = conn?.name || resolved;
          // Mark swaps so operator can see "you picked MISP over the default VirusTotal".
          return (resolved !== key) ? `${label} (swapped from ${connectorsById.get(key)?.name || key})` : label;
        })
        .filter(Boolean);
      const toastDescription = connectorList.length > 0
        ? `Agent created in Build phase. Wire up these connectors next: ${connectorList.join(', ')}.`
        : 'Agent created in Build phase. Configure connectors next.';
      addToast({ tone: 'success', title: `Installed: ${installName}`, description: toastDescription });
      setInstallCandidate(null);
      navigate(`/agents/${newAgentId}`);
    } catch (e) {
      setInstallError(e?.message || String(e));
    } finally {
      setInstalling(false);
    }
  };

  /* G206 — Submit Agentlet info modal. There is no public submit endpoint
   * yet; this surface explains the current path (contact platform admin
   * with the artefact) so the button no longer silently navigates. */
  const [submitOpen, setSubmitOpen] = useState(false);
  const openSubmit = () => setSubmitOpen(true);

  /* Facets */
  const [facets, setFacets] = useState(() => ({
    role:      new Set(),
    expertise: new Set(),
    domain:    new Set(),
  }));
  const toggleFacet = (groupKey, optionKey, nextChecked) => {
    setFacets(prev => {
      const next = new Set(prev[groupKey] || []);
      if (nextChecked) next.add(optionKey); else next.delete(optionKey);
      return { ...prev, [groupKey]: next };
    });
  };
  const clearFacets = () => setFacets({ role: new Set(), expertise: new Set(), domain: new Set() });

  const domains = useMemo(() => {
    const s = new Set();
    for (const a of agents) {
      const d = a.domain || a.category;
      if (d) s.add(d);
    }
    return Array.from(s).sort();
  }, [agents]);

  const FACET_GROUPS = useMemo(() => ([
    { key: 'role',      label: 'Role',      options: (AGENT_ROLE_CATEGORIES || []).map(r => ({ key: r.id || r.value || r, label: r.label || r.name || r })) },
    { key: 'expertise', label: 'Expertise', options: (EXPERTISE_LEVELS || []).map(e => ({ key: e.id || e.value || e, label: e.label || e.name || e })) },
    { key: 'domain',    label: 'Domain',    options: domains.map(d => ({ key: d, label: d })) },
  ]), [domains]);

  const filtered = useMemo(() => {
    return agents.filter(a => {
      if (facets.role.size > 0 && !facets.role.has(a.role || a.roleCategory)) return false;
      if (facets.expertise.size > 0 && !facets.expertise.has(a.expertise || a.expertiseLevel)) return false;
      if (facets.domain.size > 0 && !facets.domain.has(a.domain || a.category)) return false;
      return true;
    });
  }, [agents, facets]);

  const facetCounts = useMemo(() => {
    const counts = {};
    for (const g of FACET_GROUPS) {
      counts[g.key] = {};
      for (const opt of g.options) {
        counts[g.key][opt.key] = agents.filter(a => {
          for (const og of FACET_GROUPS) {
            if (og.key === g.key) continue;
            const sel = facets[og.key];
            if (!sel || sel.size === 0) continue;
            const v = og.key === 'role' ? (a.role || a.roleCategory)
                    : og.key === 'expertise' ? (a.expertise || a.expertiseLevel)
                    : (a.domain || a.category);
            if (!sel.has(v)) return false;
          }
          const v = g.key === 'role' ? (a.role || a.roleCategory)
                  : g.key === 'expertise' ? (a.expertise || a.expertiseLevel)
                  : (a.domain || a.category);
          return v === opt.key;
        }).length;
      }
    }
    return counts;
  }, [agents, facets, FACET_GROUPS]);

  const facetGroupsWithCounts = useMemo(() => (
    FACET_GROUPS.map(g => ({
      ...g,
      options: g.options.map(o => ({
        ...o,
        count: facetCounts[g.key]?.[o.key] || 0,
        checked: facets[g.key]?.has(o.key) || false,
      })),
    }))
  ), [facetCounts, facets, FACET_GROUPS]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) {
      for (const optKey of facets[g.key] || []) {
        const opt = g.options.find(o => o.key === optKey);
        chips.push({ groupKey: g.key, optionKey: optKey, label: `${g.label}: ${opt?.label || optKey}` });
      }
    }
    return chips;
  }, [facets, FACET_GROUPS]);

  /* Search */
  const [searchQuery, setSearchQuery] = useState('');
  const searched = useMemo(() => {
    if (!searchQuery.trim()) return filtered;
    const q = searchQuery.trim().toLowerCase();
    return filtered.filter(a =>
      (a.name || '').toLowerCase().includes(q) ||
      (a.description || '').toLowerCase().includes(q) ||
      (a.role || '').toLowerCase().includes(q)
    );
  }, [filtered, searchQuery]);

  /* G175 — Pagination over the searched/filtered list. Reset to page 1 whenever
   * the searched length changes so the user doesn't land on an empty page. */
  const pg = usePaginationState(10);
  const visibleRows = pg.paginate(searched);
  useEffect(() => { pg.setPage(0); }, [searched.length]);

  /* Density */
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem('agent-marketplace-density') || 'comfortable'; }
    catch { return 'comfortable'; }
  });
  const cycleDensity = () => {
    const next = density === 'comfortable' ? 'compact' : 'comfortable';
    setDensity(next);
    try { localStorage.setItem('agent-marketplace-density', next); } catch {}
  };

  /* Slideout */
  const [selected, setSelected] = useState(null);
  const [slideoutTab, setSlideoutTab] = useState('overview');
  const openSlideout = (row) => { setSelected(row); setSlideoutTab('overview'); };
  const closeSlideout = () => setSelected(null);

  /* RC-66 — relocated effects (originally written for RC-63).
   *
   * These were inlined alongside openInstall / closeInstall earlier in
   * the component body. They reference `selected` and `slideoutTab`,
   * which are declared just above — moving the effects here so the
   * deps array doesn't try to read in-TDZ const bindings at first
   * render. Same behaviour, correct hoisting.
   *
   * Effect 1: auto-sync installCandidate when the operator navigates
   * to the Install tab via the tab strip (rather than via the row's
   * Install button). Without it, Overview → Install via tab-click
   * would leave installCandidate null and the Install form would
   * render nothing.
   *
   * Effect 2: clear installCandidate + reset progress when the
   * slideout closes so stale install state doesn't leak into the
   * next open. */
  useEffect(() => {
    if (slideoutTab !== 'install') return;
    if (!selected) return;
    if (installCandidate?.id === selected.id) return;
    setInstallCandidate(selected);
    setInstallName(selected.name || '');
    setInstallStep('');
    setInstallError(null);
    /* RC-71c — Do NOT reset connectorSelections here. Pre-RC-71c we
     * reset on every install-tab activation, which wiped any swap the
     * operator had made on the Connectors tab. Selections are agent-
     * scoped and the per-agent reset happens via the selected-id effect
     * below; on the *same* agent (browse → swap → click header Install)
     * the choices must survive. The row-Install path still does a clean
     * reset via openInstall — that one starts on a potentially different
     * agent and is the right place to clear state. */
  }, [slideoutTab, selected, installCandidate]);

  /* RC-71c — Reset connectorSelections when the slideout's selected
   * agent changes. This is the single source of truth for "selections
   * are valid for THIS agent only". Covers:
   *   · Row click → row click on a different row (selected switches
   *     without closing slideout)
   *   · Row Install button on a different row (same — selected switches)
   * Does NOT fire on tab-switch within the same agent, so the
   * Connectors-tab swap → header Install flow now preserves the choices.
   * Idempotent on first open (no prior agent → no-op). */
  useEffect(() => {
    setConnectorSelections({});
  }, [selected?.id]);
  useEffect(() => {
    if (!selected && installCandidate) {
      setInstallCandidate(null);
      setInstallError(null);
      setInstallStep('');
      setConnectorSelections({});
    }
  }, [selected, installCandidate]);

  /* RC-71 — Lifted swap state for the open slideout.
   *
   * Pre-RC-71 `connectorSwapCategories` was keyed off `installCandidate`,
   * so it was empty until the operator clicked Install. The Connectors
   * tab (and the Skills tab's per-skill swap, also added in RC-71)
   * both want to render swap dropdowns while the operator is still
   * browsing — before Install is ever clicked. We compute a parallel
   * memo keyed off `selected` so both tabs can read it; runInstall and
   * the Install tab's picker still read the same `connectorSelections`
   * map, so a choice made on the Connectors tab carries through to
   * install. (When the operator clicks Install, `installCandidate` is
   * set from `selected` synchronously, so `selectedSwapCategories` and
   * the legacy `connectorSwapCategories` point at the same agent.)
   *
   * connectorsById is the O(1) lookup used by ConnectorChip and the
   * per-category card; mirrors V1 line 207-211 exactly. Built from the
   * catalogue hook which already debounces refetches, so this is cheap. */
  const connectorsById = useMemo(() => {
    const map = new Map();
    (catalogue?.connectors || []).forEach((c) => { if (c?.id) map.set(c.id, c); });
    return map;
  }, [catalogue?.connectors]);

  const selectedSwapCategories = useMemo(() => {
    if (!selected) return [];
    const connectors = selected.linkedConnectors
                    || selected.requiredConnectors
                    || selected.connectors
                    || [];
    if (!Array.isArray(connectors) || connectors.length === 0) return [];
    const seen = {};
    for (const cId of connectors) {
      const key = typeof cId === 'string' ? cId : (cId && cId.id);
      if (!key) continue;
      const alt = CONNECTOR_ALTERNATIVES?.[key];
      if (!alt) {
        // Connector has no peers in the alternation map — still surface
        // it as a single-row category so the Connectors tab lists every
        // dependency, not just the swappable ones. V1 did the same via
        // its connectorCategories memo (line 215-226).
        if (!seen[key]) seen[key] = { category: key, defaultId: key, peers: [key] };
        continue;
      }
      if (!seen[alt.category]) {
        seen[alt.category] = { category: alt.category, defaultId: key, peers: alt.peers || [key] };
      }
    }
    return Object.values(seen);
  }, [selected]);

  const stats = getAgentStats?.(agents) || {};
  const kpis = useMemo(() => ({
    available: agents.length,
    matched:   searched.length,
    domains:   domains.length,
    expertiseLevels: (EXPERTISE_LEVELS || []).length,
  }), [agents, searched, domains]);

  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1500px] mx-auto">
      <header className="flex items-start gap-4 mb-5">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight m-0">Agent Marketplace</h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            Pre-built Agentlets ready for governed orchestration · {kpis.matched} match {searchQuery ? 'search' : 'filters'}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={() => addToast({ tone: 'info', title: 'Marketplace data is static — no fetch needed.' })}>Refresh</Button>
          <Button variant="secondary" size="sm" onClick={cycleDensity}>Density: {density.charAt(0).toUpperCase() + density.slice(1)}</Button>
          <Button variant="primary" size="sm" iconLeft={<Plus size={13} />}
            onClick={openSubmit}>
            Submit Agentlet
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Available"        value={kpis.available} tone="cyan" />
        <MetricCard label="Match filters"    value={kpis.matched} tone="action" />
        <MetricCard label="Domains"          value={kpis.domains} tone="trust" />
        <MetricCard label="Expertise levels" value={kpis.expertiseLevels} tone="muted" />
      </div>

      <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-3 py-2 mb-4 flex items-center gap-2">
        <Search size={14} className="text-v2-ink-muted" />
        <input type="text" placeholder="Search by name, role, description…"
          value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 bg-transparent text-[13px] focus:outline-none text-v2-ink placeholder-v2-ink-muted" />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="text-v2-ink-muted hover:text-v2-ink"><X size={14} /></button>
        )}
      </div>

      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button key={`${c.groupKey}:${c.optionKey}`}
              onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white">
              {c.label} ×
            </button>
          ))}
          <button onClick={clearFacets} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">Clear all</button>
        </div>
      )}

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail groups={facetGroupsWithCounts} onToggle={toggleFacet} ariaLabel="Marketplace filters" />

        {searched.length === 0 ? (
          <EmptyState
            icon={<Store size={20} />}
            title={searchQuery ? 'No agents match your search' : 'No agents match filters'}
            description={searchQuery
              ? 'Try a different search term or clear the search.'
              : 'Adjust filters or clear them to see more.'}
            primaryAction={
              searchQuery
                ? <Button variant="primary" onClick={() => setSearchQuery('')}>Clear search</Button>
                : activeFilterChips.length > 0
                  ? <Button variant="primary" onClick={clearFacets}>Clear filters</Button>
                  : null
            }
          />
        ) : (
          <Table
            ariaLabel="Marketplace agents"
            density={density}
            columns={[
              { key: 'name', header: 'Agent', sortable: true, render: (r) => (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-v2-md flex items-center justify-center font-bold text-[10px] bg-v2-cyan-soft text-v2-cyan">
                    {(r.name || '??').slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-semibold text-v2-ink">{r.name}</div>
                    <div className="text-[10.5px] text-v2-ink-muted">{(r.description || '').slice(0, 70)}</div>
                  </div>
                </div>
              )},
              { key: 'role', header: 'Role', render: (r) => (
                <Pill tone="action" size="sm">{r.role || r.roleCategory || '—'}</Pill>
              )},
              { key: 'expertise', header: 'Expertise', render: (r) => {
                const exp = (r.expertise || r.expertiseLevel || '').toLowerCase();
                return <Pill tone={EXPERTISE_TONE[exp] || 'muted'} size="sm">{r.expertise || r.expertiseLevel || '—'}</Pill>;
              }},
              { key: 'domain', header: 'Domain', render: (r) => (
                <span className="text-[11.5px] text-v2-ink-secondary">{r.domain || r.category || '—'}</span>
              )},
              { key: 'skills', header: 'Skills', align: 'right', render: (r) => (
                // RC-58 — read linkedSkills (the canonical fixture key in
                // data/agentMarketplace.js) first; keep .skills as a
                // back-compat fallback for any future BE-driven shape.
                <span className="text-[12px] tabular-nums">{(r.linkedSkills || r.skills || []).length}</span>
              )},
              { key: 'connectors', header: 'Connectors', align: 'right', render: (r) => (
                // RC-58 — same: linkedConnectors is the canonical key;
                // requiredConnectors / connectors retained as fallbacks.
                <span className="text-[12px] tabular-nums">{(r.linkedConnectors || r.requiredConnectors || r.connectors || []).length}</span>
              )},
              { key: 'state', header: 'State', align: 'left', render: (r) => {
                // RC-64 — Installed-state pill. Renders the phase
                // (Build / Decide / Act) or status (Archived / Draft)
                // of the installed instance, or "—" when not installed.
                // Self-hides into a dash so non-installed rows don't
                // create visual noise in the table.
                const inst = installedByName.get(String(r.name || '').toLowerCase());
                const pill = installedStatusPill(inst);
                if (!pill) return <span className="text-[11.5px] text-v2-ink-muted">—</span>;
                return <Pill tone={pill.tone} size="sm" dot>{pill.label}</Pill>;
              }},
              { key: 'actions', header: '', align: 'right', render: (r) => {
                // RC-64 — Action morphs based on installed state.
                // If installed → "Open" linking to the agent detail page.
                // Else → "Install" as before.
                const inst = installedByName.get(String(r.name || '').toLowerCase());
                return (
                  <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    {inst?.id ? (
                      <Button variant="ghost" size="sm" iconRight={<ExternalLink size={11} />}
                        onClick={() => {
                      // RC-67 — Close the slideout BEFORE navigating, otherwise the
                      // overlay stays open over the destination page and the operator
                      // perceives "nothing happens". closeSlideout clears selected
                      // which dismisses the slideout; navigate then routes to the
                      // agent detail page.
                      closeSlideout();
                      navigate(`/agents/${inst.id}`);
                    }}>
                        Open
                      </Button>
                    ) : (
                      <Button variant="primary" size="sm"
                        onClick={() => openInstall(r)}>
                        Install
                      </Button>
                    )}
                  </div>
                );
              }},
            ]}
            rows={visibleRows}
            rowKey={(r) => r.id || r.name}
            onRowClick={openSlideout}
          />
        )}
      </div>

      {searched.length > 0 && (
        <div className="grid gap-5 mt-3 grid-cols-1 lg:grid-cols-[240px_1fr]">
          <div />
          <Pagination
            totalItems={searched.length}
            page={pg.page}
            pageSize={pg.pageSize}
            onPageChange={pg.setPage}
            onPageSizeChange={pg.setPageSize}
            noun="agents"
          />
        </div>
      )}

      {/* RC-67 — Single source of truth for installed-state derived inside
          the render. Used to: (a) toggle the Install tab label to
          "Manage", (b) swap the Install tab body from form to manage view,
          (c) drive the header CTA, (d) decide the row Open button text.
          Computed once per render against the latest installedByName /
          selected, so all four surfaces stay in lockstep. */}
      <Slideout
        isOpen={!!selected}
        onClose={closeSlideout}
        ariaLabel={selected ? `${selected.name} detail` : 'Agent detail'}
        width={520}
        tabs={(() => {
          // RC-67 — Tab label morphs based on installed state so the
          // operator sees the right verb. Skills + Connectors badges
          // now read linkedSkills / linkedConnectors (canonical fixture
          // keys per RC-58); previous values were stale undefined keys
          // that always resolved to 0.
          const inst = selected ? installedByName.get(String(selected.name || '').toLowerCase()) : null;
          /* RC-71 — Restored V1 tab parity. Tabs map 1:1 with the V1
              tab strip (Overview / Skills / Guardrails / Evals / Policy
              / Connectors) plus the V2-only Reviews placeholder and the
              install/manage surface. Counts come straight from the
              fixture arrays; the `|| undefined` collapses 0-count badges
              so the tab strip stays quiet on agents that don't declare
              guardrails or evals. */
          return [
            { id: 'overview',   label: 'Overview' },
            { id: 'skills',     label: 'Skills',
              badge: (selected?.linkedSkills || selected?.skills || []).length || undefined },
            { id: 'guardrails', label: 'Guardrails',
              badge: (selected?.guardrails || []).length || undefined },
            { id: 'evals',      label: 'Evals',
              badge: (selected?.evals || []).length || undefined },
            { id: 'policy',     label: 'Policy' },
            { id: 'connectors', label: 'Connectors',
              badge: (selected?.linkedConnectors || selected?.requiredConnectors || selected?.connectors || []).length || undefined },
            { id: 'reviews',    label: 'Reviews' },
            { id: 'install',    label: inst ? 'Manage' : 'Install' },
          ];
        })()}
        activeTabId={slideoutTab}
        onTabChange={setSlideoutTab}
        header={selected && (() => {
          // RC-64 — Detect installed state for the agent currently in
          // the slideout. When installed: show a status pill alongside
          // the role/expertise chips AND swap the "Install" CTA for an
          // "Open" link to the agent detail page (matches the table
          // row's morph so the two surfaces stay in lockstep).
          const inst = installedByName.get(String(selected.name || '').toLowerCase());
          const pill = installedStatusPill(inst);
          return (
            <div>
              {/* RC-71 — Header enrichment.
                  Pre-RC-71 the header surfaced role + expertise + an
                  optional installed-state pill. V1 also showed Tier
                  (starter/premium/enterprise) and a "v{version} by
                  {vendor}" tagline; both are governance-relevant signals
                  the operator scans during browse. Adding them here
                  keeps the existing pills in their RC-67 order so the
                  installed-state indicator is still the rightmost. */}
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Pill tone="action" size="sm">{selected.role || selected.roleCategory || selected.category || '—'}</Pill>
                <Pill tone={EXPERTISE_TONE[(selected.expertise || '').toLowerCase()] || 'muted'} size="sm">
                  {selected.expertise || selected.expertiseLevel || '—'}
                </Pill>
                <TierBadge tier={selected.tier} />
                {pill && (
                  <Pill tone={pill.tone} size="sm" dot>
                    Installed · {pill.label}
                  </Pill>
                )}
              </div>
              <h2 className="text-[18px] font-bold tracking-tight m-0">{selected.name}</h2>
              <p className="text-[12px] text-v2-ink-secondary">
                {selected.domain || selected.category || '—'}
                {selected.version && <span className="ml-2 text-v2-ink-muted">· v{selected.version}</span>}
                {selected.vendor && <span className="ml-1 text-v2-ink-muted">· by {selected.vendor}</span>}
              </p>
              <div className="flex items-center gap-2 mt-3">
                {inst?.id ? (
                  <Button variant="primary" size="sm" iconLeft={<ExternalLink size={13} />}
                    onClick={() => {
                      // RC-67 — Close the slideout BEFORE navigating, otherwise the
                      // overlay stays open over the destination page and the operator
                      // perceives "nothing happens". closeSlideout clears selected
                      // which dismisses the slideout; navigate then routes to the
                      // agent detail page.
                      closeSlideout();
                      navigate(`/agents/${inst.id}`);
                    }}>
                    Open agent
                  </Button>
                ) : (
                  <Button variant="primary" size="sm" iconLeft={<Plus size={13} />}
                    onClick={() => openInstall(selected)}>
                    Install
                  </Button>
                )}
              </div>
            </div>
          );
        })()}
      >
        {selected && slideoutTab === 'overview' && (() => {
          /* RC-71 — Overview restoration.
              Pre-RC-71 this tab rendered the short `description` plus an
              optional `useCases` block that never fires (the fixture
              doesn't have that key — added defensively pre-RC-71 for a
              future BE shape, kept here for the same reason). V1 also
              surfaced `longDescription` (multi-paragraph body), a
              4-card meta grid (role / expertise / setup / capability
              counts), the `requiredConnectors` alternation list
              ("splunk-v1 OR sentinel-v1 OR elastic-security-v1") and
              tags. All of those are governance-relevant signals during
              browse, so they come back here. The role and expertise
              cards pull their color from AGENT_ROLE_CATEGORIES /
              EXPERTISE_LEVELS so they match V1's color identity. */
          const cat = AGENT_ROLE_CATEGORIES.find(c => c.id === (selected.role || selected.roleCategory || selected.category));
          const exp = EXPERTISE_LEVELS.find(e => e.id === (selected.expertise || selected.expertiseLevel));
          const skillsCount    = (selected.linkedSkills    || selected.skills    || []).length;
          const workflowsCount = (selected.linkedWorkflows || selected.workflows || []).length;
          const guardrailsCount = (selected.guardrails || []).length;
          const evalsCount      = (selected.evals      || []).length;
          const required = selected.requiredConnectors || [];
          const tags = selected.tags || [];
          return (
            <>
              <Card padding="sm">
                <Card.Header title="About this Agentlet" />
                <Card.Body>
                  <p className="text-[12.5px] text-v2-ink leading-relaxed whitespace-pre-line">
                    {selected.longDescription || selected.description || 'No description.'}
                  </p>
                  {selected.useCases && (
                    <>
                      <h4 className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold mt-3 mb-1">Typical use cases</h4>
                      <ul className="list-disc list-inside text-[12.5px] text-v2-ink space-y-1">
                        {(Array.isArray(selected.useCases) ? selected.useCases : [selected.useCases]).map((u, i) => <li key={i}>{u}</li>)}
                      </ul>
                    </>
                  )}
                </Card.Body>
              </Card>

              <div className="grid grid-cols-2 gap-2 mt-3">
                <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-3">
                  <div className="text-[10px] uppercase tracking-wider text-v2-ink-muted font-semibold">Role category</div>
                  <div className="text-[12.5px] font-semibold mt-1" style={{ color: cat?.color || 'var(--v2-ink)' }}>
                    {cat ? `${cat.icon || ''} ${cat.name}` : (selected.role || selected.roleCategory || '—')}
                  </div>
                </div>
                <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-3">
                  <div className="text-[10px] uppercase tracking-wider text-v2-ink-muted font-semibold">Expertise level</div>
                  <div className="text-[12.5px] font-semibold mt-1" style={{ color: exp?.color || 'var(--v2-ink)' }}>
                    {exp ? exp.name : (selected.expertise || '—')}
                  </div>
                  {exp?.description && (
                    <div className="text-[11px] text-v2-ink-muted mt-0.5">{exp.description}</div>
                  )}
                </div>
                <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-3">
                  <div className="text-[10px] uppercase tracking-wider text-v2-ink-muted font-semibold">Estimated setup</div>
                  <div className="text-[12.5px] font-semibold mt-1 text-v2-ink">
                    {selected.estimatedSetupMinutes ? `~${selected.estimatedSetupMinutes} minutes` : '—'}
                  </div>
                </div>
                <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-3">
                  <div className="text-[10px] uppercase tracking-wider text-v2-ink-muted font-semibold">Capabilities</div>
                  <div className="text-[12px] font-semibold mt-1 text-v2-ink tabular-nums">
                    {skillsCount} skills · {workflowsCount} wf · {guardrailsCount} gr · {evalsCount} evals
                  </div>
                </div>
              </div>

              {required.length > 0 && (
                <Card padding="sm" className="mt-3">
                  <Card.Header title={`Required connectors · ${required.length}`} />
                  <Card.Body>
                    {/* V1 rendered each requirement as a tinted alert
                        row because the strings encode alternation
                        ("splunk-v1 OR sentinel-v1 OR elastic-security-v1")
                        — operators read them as install-time decisions,
                        not just dependencies. We mirror the line-oriented
                        layout so the OR semantics stay visible. */}
                    <div className="flex flex-col gap-1.5">
                      {required.map((req, i) => (
                        <div key={i} className="text-[12px] text-v2-ink bg-v2-warning-soft border border-v2-warning/30 rounded-v2-sm px-2.5 py-1.5">
                          <span className="font-semibold text-v2-warning">Required:</span> {req}
                        </div>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              )}

              {tags.length > 0 && (
                <Card padding="sm" className="mt-3">
                  <Card.Header title="Tags" />
                  <Card.Body>
                    <div className="flex flex-wrap gap-1.5">
                      {tags.map(t => (
                        <Pill key={t} tone="muted" size="sm">{t}</Pill>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              )}
            </>
          );
        })()}
        {selected && slideoutTab === 'skills' && (
          // RC-59 — Skills tab now renders two stacked cards: Skills
          // (existing) + Workflows (new, restored from V1 parity).
          // V1 unified skills + workflows in a single tab; V2's
          // initial redesign dropped workflow visibility entirely.
          // We restore it as a separate Card so the Skills list stays
          // visually focused while the operator can still see the
          // agent's authored workflow set. The Workflows card
          // self-hides when linkedWorkflows is empty — agents with
          // no workflows look exactly like they did pre-RC-59 (no
          // empty/placeholder card), so the change is a pure
          // additive enrichment with zero regression for the empty
          // case. Fragment wrapper is required because the parent
          // conditional now renders two siblings.
          <>
            <Card padding="sm">
              {/* RC-58 — Canonical fixture field is `linkedSkills` (see
                  data/agentMarketplace.js). Pre-RC-58 this card read
                  `.skills` only, which is undefined in the fixture data
                  → every agent rendered "Skills · 0 / No skills declared"
                  despite having a fully-populated skill list. linkedSkills
                  items are { id, name, type, connector? }; we prefer
                  `s.type` for the pill (e.g. "connector_skill", "standard")
                  and fall back to the older `s.kind` for any future
                  BE-driven shape.

                  The original `.skills` / `s.kind` fallbacks are preserved
                  so a backend that one day ships the V1 shape continues to
                  render — purely additive, zero regression risk. */}
              <Card.Header title={`Skills · ${(selected.linkedSkills || selected.skills || []).length}`} />
              <Card.Body>
                {(selected.linkedSkills || selected.skills || []).length === 0 ? (
                  <p className="text-v2-ink-muted text-[12.5px]">No skills declared.</p>
                ) : (
                  <ul className="space-y-1.5 text-[12.5px]">
                    {/* RC-71 — Polished skill row.
                        Pre-RC-71 the row read: <cyan pill with raw enum>
                        + <plain name> + <muted "· raw-connector-id">.
                        That leaked the developer enum ("connector_skill")
                        and an unresolved connector id into the operator
                        surface. We now:
                          · pipe `type` through SkillTypeBadge → human
                            label ("Connector" / "Standard" / "Workflow"
                            / "Policy") with the correct tone
                          · resolve `connector` (possibly swapped) through
                            connectorsById → ConnectorChip with icon +
                            display name
                          · render an inline swap select when the
                            connector has peers in CONNECTOR_ALTERNATIVES.
                            All same-category skills swap together so a
                            single "Splunk → Elastic" choice cascades.
                        The swap writes to the shared connectorSelections
                        map, so the Install tab sees the same selection
                        the operator made while browsing. */}
                    {(selected.linkedSkills || selected.skills || []).map((s, i) => {
                      const original = s.connector;
                      const resolved = original ? (connectorSelections[original] || original) : null;
                      const alt = original ? CONNECTOR_ALTERNATIVES?.[original] : null;
                      const hasPeers = alt && (alt.peers || []).length > 1;
                      return (
                        <li key={s.id || i} className="flex items-center gap-2 flex-wrap">
                          <SkillTypeBadge type={s.type || s.kind} />
                          <span className="text-v2-ink">{s.name || (typeof s === 'string' ? s : 'Untitled skill')}</span>
                          {resolved && (
                            <ConnectorChip connectorId={resolved} connectorsById={connectorsById} />
                          )}
                          {hasPeers && (
                            <select
                              value={resolved}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                const newVal = e.target.value;
                                setConnectorSelections(prev => {
                                  const next = { ...prev };
                                  /* Cascade: every skill (and the
                                     connectors list) in the same category
                                     swaps together so the operator can't
                                     end up with Splunk on one skill and
                                     Elastic on another. Mirrors V1 lines
                                     452-461. */
                                  (selected.linkedSkills || []).forEach(ss => {
                                    if (ss.connector && CONNECTOR_ALTERNATIVES?.[ss.connector]?.category === alt.category) {
                                      next[ss.connector] = newVal;
                                    }
                                  });
                                  (selected.linkedConnectors || []).forEach(cId => {
                                    const a = CONNECTOR_ALTERNATIVES?.[cId];
                                    if (a && a.category === alt.category) next[cId] = newVal;
                                  });
                                  return next;
                                });
                              }}
                              className="text-[10.5px] border border-v2-border rounded px-1.5 py-0.5 bg-v2-surface text-v2-action-strong cursor-pointer"
                            >
                              {(alt.peers || []).map(peerId => {
                                const peer = connectorsById.get(peerId);
                                return (
                                  <option key={peerId} value={peerId}>
                                    {peer ? `${peer.icon || ''} ${peer.name}` : peerId}
                                  </option>
                                );
                              })}
                            </select>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Card.Body>
            </Card>

            {/* RC-59 — Workflows card.
                Renders only when there is at least one entry, so the
                pre-RC-59 silent-on-empty behaviour is preserved for
                agents that don't declare workflows. Fixture entries
                are { id, name, description }; the fallback chain mirrors
                the skills card so any future BE shape with `.workflows`
                also renders. Read-only — operator can see the workflow
                set the agent will provision at install time, but the
                actual install-time picker UX lands in RC-61 along with
                the rest of the install flow. */}
            {(selected.linkedWorkflows || selected.workflows || []).length > 0 && (
              <Card padding="sm" className="mt-3">
                <Card.Header title={`Workflows · ${(selected.linkedWorkflows || selected.workflows || []).length}`} />
                <Card.Body>
                  <ul className="space-y-2 text-[12.5px]">
                    {(selected.linkedWorkflows || selected.workflows || []).map((w, i) => (
                      <li key={w.id || i} className="flex flex-col gap-0.5">
                        <div className="flex items-baseline gap-2">
                          {/* RC-59 — `brand` is a real Pill tone (cyan is
                              already used for skill kind above; brand
                              gives the workflow pill its own visual
                              channel without competing). */}
                          <Pill tone="brand" size="sm">workflow</Pill>
                          <span className="text-v2-ink font-medium">{w.name || w.id || 'Untitled workflow'}</span>
                        </div>
                        {w.description && (
                          <p className="text-[11.5px] text-v2-ink-muted leading-snug pl-1">
                            {w.description}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </Card.Body>
              </Card>
            )}
          </>
        )}
        {/* RC-71 — Guardrails tab.
            New tab restoring V1 parity. The fixture's `guardrails[]`
            array carries the agent's explicit policy promises
            (severity-typed rules like "Never auto-delete emails — only
            quarantine with human review"). V1 surfaced this as its own
            tab because these are the autonomy guarantees an operator
            most needs to see before installing. V2 dropped the entire
            tab pre-RC-71 — restoring it here with the V2 Pill primitive
            instead of V1's hand-rolled SeverityBadge. Empty-state copy
            covers agents that don't declare guardrails. */}
        {selected && slideoutTab === 'guardrails' && (
          <Card padding="sm">
            <Card.Header title={`Enforced guardrails · ${(selected.guardrails || []).length}`} />
            <Card.Body>
              {(selected.guardrails || []).length === 0 ? (
                <p className="text-v2-ink-muted text-[12.5px]">No guardrails declared.</p>
              ) : (
                <ul className="space-y-2 text-[12.5px]">
                  {selected.guardrails.map((g, i) => (
                    <li key={g.id || i} className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-2.5">
                      <div className="flex items-center gap-2 mb-1">
                        <SeverityBadge severity={g.severity} />
                        <span className="font-mono text-[10.5px] text-v2-ink-muted">{g.id}</span>
                      </div>
                      <p className="text-[12.5px] text-v2-ink leading-relaxed">{g.rule}</p>
                    </li>
                  ))}
                </ul>
              )}
            </Card.Body>
          </Card>
        )}

        {/* RC-71 — Evals tab.
            New tab restoring V1 parity. The fixture's `evals[]` array
            describes the agent's quality contract (e.g. "Known Phish
            Detection — target >98% on 50 known phishing samples"). V1
            surfaced this so operators could see what the agent has
            been tested against before installing. V2 dropped the tab
            pre-RC-71. Each row shows the eval-type pill (functional /
            negative / guardrail / security / cost) + name + the
            description text explaining the success criterion. */}
        {selected && slideoutTab === 'evals' && (
          <Card padding="sm">
            <Card.Header title={`Evaluation tests · ${(selected.evals || []).length}`} />
            <Card.Body>
              {(selected.evals || []).length === 0 ? (
                <p className="text-v2-ink-muted text-[12.5px]">No evaluation tests declared.</p>
              ) : (
                <ul className="space-y-2 text-[12.5px]">
                  {selected.evals.map((ev, i) => (
                    <li key={ev.name || i} className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-2.5">
                      <div className="flex items-center gap-2 mb-1">
                        <EvalTypeBadge type={ev.type} />
                        <span className="font-semibold text-v2-ink">{ev.name}</span>
                      </div>
                      {ev.description && (
                        <p className="text-[12px] text-v2-ink-secondary leading-relaxed">{ev.description}</p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Card.Body>
          </Card>
        )}

        {/* RC-71 — Policy tab.
            New tab restoring V1 parity. The fixture's `policyConfig{}`
            is the agent's actual autonomy contract: which actions are
            allowed / blocked, which require human approval, which are
            auto-approved, plus per-agent thresholds and special-case
            rules (VIP escalation, rate limits, etc.).
            We render the four most-trafficked arrays as tinted chip
            groups; the remainder of the object (thresholds, allowedX
            counts, VIP rules) goes into a collapsed <details> block so
            it's available without dominating the surface. Each chip
            group self-hides when its array is missing / empty so agents
            with a simpler policy don't render empty headings.

            Tone reasoning: approval-required = danger (this is the gate
            that holds back autonomy), auto-approved = success (these
            are pre-blessed), allowed = action (interactive scope),
            blocked = muted strikethrough (cannot do these). */}
        {selected && slideoutTab === 'policy' && (() => {
          const pc = selected.policyConfig || {};
          const knownArrayKeys = ['approvalRequired', 'autoApproved', 'allowedActions', 'blockedActions'];
          const remaining = Object.entries(pc).filter(([k]) => !knownArrayKeys.includes(k));
          const renderGroup = (label, tone, items, opts = {}) => {
            if (!Array.isArray(items) || items.length === 0) return null;
            return (
              <div className="mb-3">
                <div className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1.5">{label}</div>
                <div className="flex flex-wrap gap-1.5">
                  {items.map(action => (
                    <Pill key={action} tone={tone} size="sm" className={opts.strike ? 'line-through' : ''}>{action}</Pill>
                  ))}
                </div>
              </div>
            );
          };
          return (
            <Card padding="sm">
              <Card.Header title="Policy configuration" />
              <Card.Body>
                <p className="text-[11.5px] text-v2-ink-muted mb-3">
                  These policies control what this Agentlet can and cannot do autonomously. After install you can edit them in Agent Lifecycle to match your organization's risk tolerance.
                </p>
                {renderGroup('Actions requiring approval', 'danger',  pc.approvalRequired)}
                {renderGroup('Auto-approved actions',     'success', pc.autoApproved)}
                {renderGroup('Allowed actions',           'action',  pc.allowedActions)}
                {renderGroup('Blocked actions',           'muted',   pc.blockedActions, { strike: true })}
                {remaining.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-[11.5px] text-v2-ink-secondary cursor-pointer select-none">
                      Additional policy settings ({remaining.length})
                    </summary>
                    <pre className="mt-2 text-[10.5px] bg-v2-surface border border-v2-border-subtle rounded-v2-sm p-2 font-mono text-v2-ink overflow-x-auto whitespace-pre-wrap">
{JSON.stringify(Object.fromEntries(remaining), null, 2)}
                    </pre>
                  </details>
                )}
                {Object.keys(pc).length === 0 && (
                  <p className="text-v2-ink-muted text-[12.5px]">No policy configuration declared.</p>
                )}
              </Card.Body>
            </Card>
          );
        })()}

        {selected && slideoutTab === 'connectors' && (() => {
          /* RC-71 — Connectors tab upgrade.
              Pre-RC-71 this tab rendered the linkedConnectors array as a
              flat row of muted Pill chips containing raw id strings
              ("virustotal-v1"). V1 used the registry to resolve each
              connector into icon + display name + vendor + version +
              skill-count, AND grouped by category so the swap-peers
              relationship was visible at-a-glance. V2 lost that
              entirely. We rebuild it here using `selectedSwapCategories`
              (a categorisation memo lifted from the Install tab so
              both surfaces share state) + `connectorsById` for O(1)
              registry resolution.

              Swap UX: each category card with peers > 1 renders an
              inline select. Selections flow into the shared
              `connectorSelections` map — runInstall reads from the
              same map, so a swap choice made while browsing carries
              through to install without the operator re-picking. */
          const conns = selected.linkedConnectors || selected.requiredConnectors || selected.connectors || [];
          if (conns.length === 0) {
            return (
              <Card padding="sm">
                <Card.Header title="Required connectors · 0" />
                <Card.Body>
                  <p className="text-v2-ink-muted text-[12.5px]">No connector dependencies.</p>
                </Card.Body>
              </Card>
            );
          }
          return (
            <>
              <div className="bg-v2-trust-soft border border-v2-trust/30 text-v2-trust rounded-v2-sm px-3 py-2 mb-3 text-[11.5px]">
                <span className="font-semibold">Flexible connectors:</span> swap tools within the same category before install. Choices made here apply across every skill that uses that connector and carry through to the Install step.
              </div>
              <Card padding="sm">
                <Card.Header title={`Connector configuration · ${selectedSwapCategories.length}`} />
                <Card.Body>
                  <ul className="space-y-2">
                    {selectedSwapCategories.map(({ category: catName, defaultId, peers }) => {
                      const selectedId = connectorSelections[defaultId] || defaultId;
                      const conn = connectorsById.get(selectedId);
                      const hasPeers = (peers || []).length > 1;
                      return (
                        <li key={catName} className={`rounded-v2-md p-2.5 border ${hasPeers ? 'border-v2-trust/40 bg-v2-trust-soft/30' : 'border-v2-border-subtle bg-v2-surface'}`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[18px] leading-none" aria-hidden>{conn?.icon || '🔗'}</span>
                            <div className="flex-1 min-w-0">
                              <div className="text-[13px] font-semibold text-v2-ink truncate">{conn?.name || selectedId}</div>
                              <div className="text-[10.5px] text-v2-ink-muted truncate">
                                {(conn?.vendor || 'Unknown vendor')} · v{conn?.version || '?'} · {(conn?.skills?.length ?? 0)} skills
                              </div>
                            </div>
                            <Pill tone="muted" size="sm">{catName}</Pill>
                            {hasPeers ? (
                              <select
                                value={selectedId}
                                onChange={(e) => {
                                  const newVal = e.target.value;
                                  setConnectorSelections(prev => {
                                    const next = { ...prev };
                                    /* Cascade to every connector + skill
                                       in the same category — keeps the
                                       agent internally consistent. */
                                    (selected.linkedConnectors || []).forEach(cId => {
                                      const a = CONNECTOR_ALTERNATIVES?.[cId];
                                      if (a && a.category === catName) next[cId] = newVal;
                                    });
                                    (selected.linkedSkills || []).forEach(ss => {
                                      if (ss.connector && CONNECTOR_ALTERNATIVES?.[ss.connector]?.category === catName) {
                                        next[ss.connector] = newVal;
                                      }
                                    });
                                    return next;
                                  });
                                }}
                                className="text-[11px] border border-v2-border rounded px-1.5 py-0.5 bg-v2-surface text-v2-action-strong cursor-pointer"
                              >
                                {(peers || []).map(peerId => {
                                  const peer = connectorsById.get(peerId);
                                  return (
                                    <option key={peerId} value={peerId}>
                                      {peer ? `${peer.icon || ''} ${peer.name}` : peerId}
                                    </option>
                                  );
                                })}
                              </select>
                            ) : (
                              <span className="text-[10.5px] text-v2-ink-muted">No alternates</span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </Card.Body>
              </Card>
            </>
          );
        })()}
        {selected && slideoutTab === 'reviews' && (
          <Card padding="sm">
            <Card.Header title="Reviews" />
            <Card.Body>
              <p className="text-v2-ink-muted text-[12.5px]">
                Community reviews are not yet enabled in this release. Once the review service ships,
                aggregated ratings, deployment counts, and operator feedback for this Agentlet will appear here.
              </p>
            </Card.Body>
          </Card>
        )}
        {/* RC-63 — Install tab is now the single install surface.
            Pre-RC-63 this tab just rendered an impact summary + a
            button that opened a SEPARATE Modal with the real form —
            three problems flowed from that: (1) two surfaces (browse
            slideout + install modal) to context-switch between,
            (2) two Install entry points (row button + tab) with no
            clear primary, (3) the connector picker was hidden inside
            the Modal so operators couldn't see swap existed until
            commit-time. Now the form lives here: name + picker +
            impact summary + Install/Cancel. The row's green Install
            button calls openInstall which opens the slideout to this
            tab directly. Operators can also browse Overview → Skills
            → Connectors → switch to Install when ready. One surface,
            one mental model. */}
        {/* RC-69 — Install tab MANAGE branch.
            When the agent is already installed in this tenant, show a
            "Manage" card instead of the install form so the operator
            isn't prompted to install a copy they already have. The
            tab label itself was switched to "Manage" in RC-67. This
            block fully replaces the install form — operator gets:
              · Installed-state banner with phase pill
              · "Open agent" CTA (closes slideout, navigates to detail)
              · "Install another copy" link for the rare case the
                operator genuinely wants a second instance (e.g. a
                Test or QA copy alongside the existing one).
            The install form below renders only when no installed
            instance matches the catalog row's name. */}
        {selected && slideoutTab === 'install' && installCandidate && (() => {
          const inst = installedByName.get(String(installCandidate.name || '').toLowerCase());
          if (inst?.id) {
            const pill = installedStatusPill(inst);
            return (
              <Card padding="sm">
                <Card.Header title={`Manage: ${installCandidate.name || 'Agentlet'}`} />
                <Card.Body>
                  <div className="space-y-3.5">
                    <div className="rounded-v2-md border border-v2-success-border bg-v2-success-soft/40 p-3 text-[12.5px] text-v2-ink leading-relaxed">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold">Already installed in your tenant</span>
                        {pill && <Pill tone={pill.tone} size="sm" dot>{pill.label}</Pill>}
                      </div>
                      <div className="text-v2-ink-secondary">
                        Installed as <strong className="text-v2-ink">{inst.name}</strong>. Open the agent record to configure connectors, manage skills, advance phases, or publish.
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="primary" size="sm" iconLeft={<ExternalLink size={13} />}
                        onClick={() => {
                          // RC-69 — Mirror RC-67 Edit 2: close the slideout
                          // before navigating so the destination page isn't
                          // covered by the overlay.
                          closeSlideout();
                          navigate(`/agents/${inst.id}`);
                        }}>
                        Open agent
                      </Button>
                      <Button variant="ghost" size="sm"
                        onClick={() => setSlideoutTab('overview')}
                        disabled={installing}>
                        Back to overview
                      </Button>
                    </div>
                    <details className="text-[11.5px] text-v2-ink-muted">
                      <summary className="cursor-pointer hover:text-v2-ink">
                        Need a second copy? (advanced)
                      </summary>
                      <div className="mt-2 space-y-2 pl-1">
                        <p>
                          Most teams use one instance per Agentlet. If you need a
                          separate test / QA copy, you can install another instance
                          under a different name — it'll be a brand-new agent in your
                          tenant.
                        </p>
                        <label className="block">
                          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">New name for the second copy</span>
                          <input
                            type="text"
                            className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] text-v2-ink"
                            value={installName}
                            onChange={(e) => setInstallName(e.target.value)}
                            disabled={installing}
                            placeholder={`${installCandidate.name} (copy)`}
                          />
                        </label>
                        <Button variant="ghost" size="sm" iconLeft={<Plus size={13} />}
                          onClick={runInstall}
                          disabled={installing || !installName.trim() || installName.trim().toLowerCase() === String(inst.name || '').toLowerCase()}
                          title={installName.trim().toLowerCase() === String(inst.name || '').toLowerCase()
                            ? 'Pick a different name from the existing install'
                            : 'Provision a second instance under the new name'}>
                          {installing ? 'Installing…' : 'Install another copy'}
                        </Button>
                        {installStep && (
                          <div className="rounded-v2-md border border-v2-action-border bg-v2-action-soft text-v2-action-strong p-2.5 text-[11.5px]">
                            {installStep}
                          </div>
                        )}
                        {installError && (
                          <div className="rounded-v2-md border border-v2-danger-border bg-v2-danger-soft text-v2-danger-strong p-2.5 text-[11.5px]">
                            {installError}
                          </div>
                        )}
                      </div>
                    </details>
                  </div>
                </Card.Body>
              </Card>
            );
          }
          // ─── Not installed — render the install form (unchanged from RC-63) ──
          return (
          <Card padding="sm">
            <Card.Header title={`Install: ${installCandidate.name || 'Agentlet'}`} />
            <Card.Body>
              <div className="space-y-3.5">
                {installCandidate.description && (
                  <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed">
                    {installCandidate.description}
                  </p>
                )}
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Agent name in your tenant</span>
                  <input
                    type="text"
                    className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] text-v2-ink"
                    value={installName}
                    onChange={(e) => setInstallName(e.target.value)}
                    disabled={installing}
                  />
                </label>

                {/* RC-61 — Connector swap picker. Self-hides when
                    the candidate has no swappable connectors. */}
                {connectorSwapCategories.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">
                      Pick your connectors
                    </div>
                    <p className="text-[11.5px] text-v2-ink-secondary leading-snug">
                      Defaults are pre-selected from this Agentlet. Swap any category if you already use an alternative — the install will bind skills to your pick.
                    </p>
                    <div className="space-y-1.5">
                      {connectorSwapCategories.map(({ category, defaultId, peers }) => {
                        const currentValue = connectorSelections[defaultId] || defaultId;
                        return (
                          <label key={category} className="flex items-center gap-2 text-[12.5px]">
                            <span className="text-v2-ink-muted w-44 flex-shrink-0">{category}</span>
                            <select
                              className="flex-1 bg-v2-surface border border-v2-border rounded-v2-md px-2 py-1 text-[12.5px] text-v2-ink"
                              value={currentValue}
                              onChange={(e) => setConnectorSelections(prev => ({
                                ...prev,
                                [defaultId]: e.target.value,
                              }))}
                              disabled={installing || peers.length <= 1}
                            >
                              {peers.map(pid => (
                                <option key={pid} value={pid}>
                                  {pid}{pid === defaultId ? ' (default)' : ''}
                                </option>
                              ))}
                            </select>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="rounded-v2-md border border-v2-border bg-v2-muted/40 p-3 text-[12px]">
                  <div className="font-semibold text-v2-ink mb-1">This will:</div>
                  <ul className="list-disc list-inside space-y-0.5 text-v2-ink-secondary">
                    <li>Create a new Agentlet in <strong>Build</strong> phase</li>
                    <li>
                      Provision {(installCandidate.linkedSkills || installCandidate.skills || []).length} skill
                      {(installCandidate.linkedSkills || installCandidate.skills || []).length !== 1 ? 's' : ''}
                    </li>
                    <li>
                      Require {(installCandidate.linkedConnectors || installCandidate.requiredConnectors || installCandidate.connectors || []).length}
                      {' '}connector{(installCandidate.linkedConnectors || installCandidate.requiredConnectors || installCandidate.connectors || []).length !== 1 ? 's' : ''} to be configured before the agent can run
                    </li>
                  </ul>
                </div>

                {installStep && (
                  <div className="rounded-v2-md border border-v2-action-border bg-v2-action-soft text-v2-action-strong p-2.5 text-[12px]">
                    {installStep}
                  </div>
                )}
                {installError && (
                  <div className="rounded-v2-md border border-v2-danger-border bg-v2-danger-soft text-v2-danger-strong p-3 text-[12.5px]">
                    {installError}
                  </div>
                )}

                <div className="flex items-center gap-2 pt-1">
                  <Button variant="primary" size="sm" iconLeft={<Plus size={13} />}
                    onClick={runInstall}
                    disabled={installing || !installName.trim()}>
                    {installing ? 'Installing…' : 'Install'}
                  </Button>
                  <Button variant="ghost" size="sm"
                    onClick={() => setSlideoutTab('overview')}
                    disabled={installing}>
                    Back to overview
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
          );
          // ─── end RC-69 IIFE: select install vs manage branch ───
        })()}
      </Slideout>

      {/* RC-63 — Install modal removed.
          The form previously rendered here now lives inside the
          slideout's Install tab (search "RC-63 — Install tab is now
          the single install surface" above). Operators reach it via
          either the row's green Install button (auto-opens slideout
          → Install tab via openInstall) or the slideout tab nav
          (auto-syncs installCandidate via the useEffect). One
          surface, one mental model. */}

      {/* G206 — Submit Agentlet info modal */}
      <Modal
        isOpen={submitOpen}
        onClose={() => setSubmitOpen(false)}
        title="Submit an Agentlet"
        footer={
          <div className="flex items-center justify-end">
            <Button variant="primary" size="sm" onClick={() => setSubmitOpen(false)}>Got it</Button>
          </div>
        }
      >
        <div className="space-y-3 text-[12.5px] text-v2-ink leading-relaxed">
          <p>
            The marketplace currently lists curated Agentlets shipped by the platform team. Community
            submissions are not yet self-service.
          </p>
          <p>
            To propose a new marketplace Agentlet, export your authored agent definition from the
            Agent Detail page (Build tab → Export) and share the artefact with your platform administrator.
            They'll review it and submit it to the next marketplace release.
          </p>
          <p className="text-v2-ink-muted">
            Self-service submission with eval-attestation gating is on the roadmap; this modal will be
            replaced with that flow once it ships.
          </p>
        </div>
      </Modal>
    </div>
  );
}

export default function ResolvedAgentMarketplacePage(props) {
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const legacy = url?.searchParams?.get('legacy') === '1';
  if (legacy || !isRebirthBuildSectionEnabled()) return <AgentMarketplacePage {...props} />;
  return <AgentMarketplacePageV2Inner {...props} />;
}

export { AgentMarketplacePageV2Inner };
